import {useEffect, useMemo, useState} from 'react'
import {routes} from '../apis/routes'
import {useNavDispatchContext} from '../contexts/NavContext'
import {LOADING_STATE} from '../reducers/NavReducer'
import {
  captureDeferredInitializationError,
  shouldDeferErrorsToLeadCapture,
} from './deferredInitializationError'
import {useAxios} from './useAxios'
import useConfig from './useConfig'

export const useCommonDependencies = () => {
  const navDispatch = useNavDispatchContext()
  const [a2zApi, isApiReady] = useAxios()
  const {dealership} = useConfig()

  const [dealershipData, setDealershipData] = useState(null)
  const [requestTypes, setRequestTypes] = useState(null)

  const [isFatalError, setIsFatalError] = useState(false)
  const [isLangReady, setIsLangReady] = useState(false)

  const {
    dealerships: {
      includes,
      paths: {dealership: dealershipRoute},
    },
  } = routes

  const dealershipPath = useMemo(
    () =>
      dealershipRoute(dealership?.id, [
        includes.ANALYTICS_CONFIGURATION,
        includes.CREDIT_APP_CONFIGURATION,
        includes.DR_SHARE_QUOTE,
        includes.DR_SAVE_DEAL,
        includes.DR_DOC_UPLOADS,
        includes.DR_MIN_AMOUNT_FINANCED,
        includes.DR_CDP_FULL_PATH_ENABLED,
      ]),
    [dealership?.id]
  )

  const setupDealershipData = () => {
    if (!dealership?.id) {
      return
    }

    const dealershipDataStored = localStorage.getItem(dealershipPath)

    if (!dealershipDataStored) {
      return
    }

    setDealershipData(JSON.parse(dealershipDataStored))
  }

  const setupLang = () => {
    const langStored = localStorage.getItem(routes.lang.base)

    if (!langStored) {
      return
    }

    window.A2Z_LANG = JSON.parse(langStored)
    setIsLangReady(true)
  }

  const setupRequestTypes = () => {
    const requestTypeStored = localStorage.getItem(routes.customers.paths.leadsRequestTypes())

    if (!requestTypeStored) {
      return
    }

    setRequestTypes(JSON.parse(requestTypeStored).data)
  }

  const handleFetchError = (source, err) => {
    if (shouldDeferErrorsToLeadCapture()) {
      captureDeferredInitializationError(err, source)
    } else {
      navDispatch({type: 'setLoading', payload: LOADING_STATE.FAILED})
      setIsFatalError(true)

      return
    }

    if (source === 'lang' && !isLangReady) {
      const langStored = localStorage.getItem(routes.lang.base)

      if (langStored) {
        globalThis.A2Z_LANG = JSON.parse(langStored)
        setIsLangReady(true)

        return
      }

      if (shouldDeferErrorsToLeadCapture()) {
        globalThis.A2Z_LANG = globalThis.A2Z_LANG || {}
        setIsLangReady(true)

        return
      }

      setIsFatalError(true)
    }

    if (source === 'request_types' && !requestTypes) {
      setRequestTypes([])
    }
  }

  const handleFetchDealershipData = () => {
    if (dealershipData || !dealership?.id) {
      return
    }

    a2zApi
      .get(dealershipPath)
      .then(({data: dealershipDataResponse}) => {
        localStorage.setItem(dealershipPath, JSON.stringify(dealershipDataResponse))
        setDealershipData(dealershipDataResponse)
      })
      .catch((err) => handleFetchError('dealership', err))
  }

  const handleFetchLang = () => {
    if (isLangReady) {
      return
    }

    a2zApi
      .get(routes.lang.base)
      .then(({data: lang}) => {
        window.A2Z_LANG = lang
        localStorage.setItem(routes.lang.base, JSON.stringify(lang))
        setIsLangReady(true)
      })
      .catch((err) => handleFetchError('lang', err))
  }

  const handleFetchRequestTypes = () => {
    if (!requestTypes) {
      a2zApi
        .get(routes.customers.paths.leadsRequestTypes())
        .then(({data: requestTypesData}) => {
          localStorage.setItem(
            routes.customers.paths.leadsRequestTypes(),
            JSON.stringify(requestTypesData)
          )
          setRequestTypes(requestTypesData.data)
        })
        .catch((err) => handleFetchError('request_types', err))
    }
  }

  useEffect(() => {
    setupLang()
    setupRequestTypes()
    setupDealershipData()
  }, [dealership?.id])

  useEffect(() => {
    if (!isApiReady) {
      return
    }

    window.onbeforeunload = () => {
      localStorage.clear()
    }

    handleFetchLang()
    handleFetchRequestTypes()
    handleFetchDealershipData()
  }, [isApiReady, dealership?.id])

  return {
    data: {dealershipData, requestTypes},
    isError: isFatalError,
    isLoading: !isLangReady || !requestTypes,
  }
}
